import { DestroyRef, Inject, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  EntityApplications,
  RoleEntityPermissionMap,
  UserRole,
  UserRoleModelKeys,
  ChangeSourceType,
  Entity,
  LogMessageTypes,
  LogMessageSourceTypes,
} from 'ag-common-lib/public-api';
import { LoggerService } from 'ag-common-svc/lib/services/logger.service';
import { UserPermissionService } from 'ag-common-svc/lib/services/user-permissions.service';
import { AgentService, AuthService, EffectivePermissionsService } from 'ag-common-svc/public-api';
import type { EffectivePermissions } from 'ag-common-svc/lib/services/effective-permissions.service';
import { FirebaseApp } from 'firebase/app';
import { getAuth, signOut } from 'firebase/auth';
import { ToastrService } from 'ngx-toastr';
import { filter, firstValueFrom, map, Observable, pairwise, shareReplay, timeout } from 'rxjs';
import { UserRolesService } from 'ag-common-svc/lib/services/user-roles.service';
import { AuthLogService } from 'ag-common-svc/lib/services/auth-log.service';
import { AppRoutes } from 'src/app/app.model';
import { AgentsRoutes } from 'src/app/ag-crm/agents/agents.model';
import { FIREBASE_APP } from 'ag-common-svc/lib/injections/firebase-app';
import { hasPermissionGuard } from 'ag-common-svc/shared/utils/has-permission-guard';
import { parkSignOutNotice } from './sign-out-notice';
import { CampaignsRoutes } from 'src/app/ag-campaigns/ag-campaigns.model';
import { NotificationsHubRoutes } from 'src/app/ag-notifications-hub/ag-notifications-hub.model';
import { AdminCrmRoutes } from 'src/app/admin-crm/admin-crm.model';

@Injectable()
export class ARMAuthService extends AuthService {
  readonly armUserRoles$: Observable<UserRole[]>;
  private readonly effectivePermissions = inject(EffectivePermissionsService);
  private readonly destroyRef = inject(DestroyRef);
  readonly armPermissions$: Observable<RoleEntityPermissionMap>;
  private readonly _armHomePathListByPriority = [
    {
      path: [AppRoutes.Dashboard],
      entity: [Entity.dashboard],
    },
    {
      path: [AppRoutes.Agents],
      entity: [Entity.agentAdminister],
    },
    {
      path: [AppRoutes.Agencies],
      entity: [Entity.agencyAdminister],
    },
    {
      path: [AppRoutes.Carriers],
      entity: [Entity.carrierCrm],
    },
    {
      path: [AppRoutes.Prospects],
      entity: [Entity.prospect],
    },
    {
      path: [AppRoutes.Registrants],
      entity: [Entity.registrants],
    },
    {
      path: [AppRoutes.Events],
      entity: [Entity.conference, Entity.conferenceListAdmin, Entity.conferenceList],
    },
    {
      path: [AppRoutes.ConferenceRegistrations],
      entity: [Entity.conferenceRegistrants],
    },
    {
      path: [AppRoutes.Campaign, CampaignsRoutes.CampaignServices],
      entity: [Entity.campaignServices],
    },
    {
      path: [AppRoutes.Campaign, CampaignsRoutes.CampaignManagementTaskBoard],
      entity: [Entity.campaignTaskBoard],
    },
    {
      path: [AppRoutes.Campaign, CampaignsRoutes.CampaignManagementTaskGrid],
      entity: [Entity.campaignTaskGrid],
    },
    {
      path: [AppRoutes.NotificationsHub, NotificationsHubRoutes.NotificationsScheduler],
      entity: [Entity.notificationsScheduler],
    },
    {
      path: [AppRoutes.NotificationsHub, NotificationsHubRoutes.NotificationsTemplates],
      entity: [Entity.notificationsTemplates],
    },
    {
      path: [AppRoutes.NotificationsHub, NotificationsHubRoutes.NotificationsChannels],
      entity: [Entity.notificationsChannels],
    },
    {
      path: [AppRoutes.NotificationsHub, NotificationsHubRoutes.DynamicLists],
      entity: [Entity.dynamicListGroups],
    },
    {
      path: [AppRoutes.NotificationsHub, NotificationsHubRoutes.DynamicSegments],
      entity: [Entity.dynamicSegmentsGroups],
    },
    {
      path: [AppRoutes.Forms],
      entity: [Entity.reportChristmasCardList],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.TaskListManager],
      entity: [Entity.administrationTaskListManager],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.ImportRulesManager],
      entity: [Entity.administrationImportRulesManager],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.LookupsManager],
      entity: [Entity.administrationLookupsManager],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.ErrorsManager],
      entity: [Entity.administrationErrorsManager],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.LogsManager],
      entity: [Entity.administrationLogsManager],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.UserRolesManager],
      entity: [Entity.administrationUserManager],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.ReportsManager],
      entity: [Entity.administrationReportsManager],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.AgentSalesGoals],
      entity: [Entity.agentSalesGoal],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.MgaSalesGoals],
      entity: [Entity.mgaSalesGoal],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.RoleManager],
      entity: [Entity.administrationRoleManager],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.GoogleAuthManagement],
      entity: [Entity.administrationGoogleAuthManager],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.AuthStats],
      entity: [Entity.administrationAuthStats],
    },
    {
      path: [AppRoutes.Administration, AdminCrmRoutes.AgentMergeTool],
      entity: [Entity.administrationMergeTool],
    },
    /**
     * Last, so it only ever wins for someone whose grant really is just the
     * registry. Without an entry they fell through to the Agents list, which their
     * role does not open either, and the first thing they saw after signing in was
     * Access Denied.
     */
    {
      path: [AppRoutes.AgentRegistry],
      entity: [Entity.agentRegistry],
    },
  ];

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,

    private toastrService: ToastrService,

    ngZone: NgZone,
    router: Router,
    route: ActivatedRoute,
    agentService: AgentService,
    loggerService: LoggerService,
    userPermissionService: UserPermissionService,
    userRolesService: UserRolesService,
    authLogService: AuthLogService,
  ) {
    super(
      fireBaseApp,
      ngZone,
      router,
      route,
      toastrService,
      agentService,
      loggerService,
      userPermissionService,
      userRolesService,
      authLogService,
    );
    this.afterLogoutRedirectPath = 'login-form';

    this.auth = getAuth(fireBaseApp);

    // Same filter as before; the list underneath now comes from the identity API.
    this.armUserRoles$ = this.userRoles$.pipe(
      map((userRoles: UserRole[]) =>
        userRoles.filter(
          role => role[UserRoleModelKeys.isEnable] && role[UserRoleModelKeys.appId] === EntityApplications.crm,
        ),
      ),
      shareReplay(1),
    );

    /**
     * The OR-merge now happens server-side, so "effective permissions" means the same
     * thing to the API enforcing a call and to the UI deciding whether to offer it.
     * The fold that used to live here (and in three other services) is gone.
     */
    this.armPermissions$ = this.effectivePermissions
      .forCurrentUser(EntityApplications.crm)
      .pipe(
        map(response => response.permissions),
        shareReplay(1),
      );

    /**
     * Live revocation. The permission map re-reads whenever the user-roles signal
     * moves, so a change made in the Role Manager reaches an open session in seconds
     * instead of at next login.
     *
     * Compared pairwise, because only a session that HAD access can have it taken
     * away. Counting emissions instead was wrong: an unresolved state arrives before
     * the first real answer, so for a user who never had access the first real
     * answer looked like a revocation — they were signed out mid-login, and the
     * "Access Denied" notice sign-in had just raised went down with the page.
     *
     * Unresolved reads are filtered out rather than compared: a failed read also
     * yields an empty map, and treating that as "lost access" would sign people out
     * over a transient API error.
     *
     * Two outcomes, in order of severity: losing every CRM role means losing the
     * right to be in this app at all, which is the same condition
     * `checkARMPermissions` gates at login; otherwise the current screen is
     * re-checked and abandoned if it is no longer permitted. Template pipes and
     * guards pick the rest up on their own, because they were already observable.
     */
    this.effectivePermissions
      .forCurrentUser(EntityApplications.crm)
      .pipe(
        filter(response => response.resolved),
        pairwise(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([previous, current]) => this.handlePermissionsRevoked(previous, current));

    // Set ARM landing page based on permission
    this.homePathCommands$ = this.armPermissions$.pipe(
      map(
        permissionsMap =>
          this._armHomePathListByPriority.find(item => hasPermissionGuard(item.entity, permissionsMap))?.path ?? [
            AppRoutes.Agents,
            AgentsRoutes.AgentsList,
          ],
      ),
      shareReplay(1),
    );
  }

  public async signInWithEmailAndPassword(email: string, password: string) {
    await super.signInWithEmailAndPassword(email, password);

    let agent: Awaited<ReturnType<typeof this.getLoggedInAgentOnce>>;
    let hasPermissions = false;
    try {
      agent = await this.getLoggedInAgentOnce();
      // Only worth asking about roles once we know there is someone to have them.
      hasPermissions = agent ? await this.checkARMPermissions() : false;
    } catch (error) {
      /**
       * We never found out whether this user has access, which is not the same as
       * finding out they do not. Refusing them here would tell them something untrue
       * and send them to support with the wrong question; the sign-in is abandoned with
       * a retry instead, and the session ended so nothing is left half-open.
       */
      const errorLogCode = await this.loggerService.createLog(
        LogMessageTypes.login,
        LogMessageSourceTypes.email,
        email,
        'Timed out resolving agent or roles during ARM sign in.',
        { error },
      );

      parkSignOutNotice({
        title: 'Sign In Failed',
        message: `<br/><p>We could not verify your access just now. Please try again.</p><p>If the problem continues, please contact Alliance Group for Assistance with this code: ${errorLogCode}</p>`,
      });

      await this.logOut();

      return;
    }

    /**
     * Authenticated by Firebase, but absent from the agents projection — the state a
     * user is in before anyone has provisioned them. Called out separately from the
     * no-roles refusal below because the remedy differs: this one needs an agent
     * record created, not a role granted, and a log line saying "no User Permissions"
     * would send support looking for the wrong thing.
     */
    if (!agent) {
      const errorLogCode = await this.loggerService.createLog(
        LogMessageTypes.login,
        LogMessageSourceTypes.email,
        email,
        'No agent record found for authenticated user during ARM sign in.',
      );

      parkSignOutNotice({
        title: 'Access Denied',
        message: `<br/><p>Your account does not have access to this application.</p><p>Please contact Alliance Group for Assistance with this code: ${errorLogCode}</p>`,
      });

      await this.logOut();

      return;
    }

    if (!hasPermissions) {
      const errorLogCode = await this.loggerService.createLog(
        LogMessageTypes.login,
        LogMessageSourceTypes.email,
        email,
        'Agent have no User Permissions to access ARM.',
      );
      /**
       * Parked rather than raised here: the sign-out below reloads the page, which
       * would take this notice with it. The login form raises it once the reload
       * has settled, so the refusal is still on screen to read.
       */
      parkSignOutNotice({
        title: 'Access Denied',
        message: `<br/> <p>Permissions to Access ARM has not been granted.</p><p>Please contact Alliance Group for Assistance with this code: ${errorLogCode}</p>`,
      });

      // Signed in to Firebase but refused by this app: end the session rather than
      // leaving it half-open behind the login form.
      await this.logOut();

      return;
    }

    try {
      const actionName = ChangeSourceType.regularArm;
      await this.logUserIntoPortal(actionName);
    } catch (error) {}

    await this.navigateForward();
  }

  /**
   * Uses the same route/entity table the landing-page picker uses, so "may this user
   * be on this screen" is answered by one definition rather than two that can drift.
   * A URL with no entry is left alone: an unmapped route was never gated by this
   * table in the first place.
   */
  private async handlePermissionsRevoked(
    previous: EffectivePermissions,
    current: EffectivePermissions,
  ): Promise<void> {
    const permissionsMap = current.permissions;
    const stillHasCrmAccess = Object.keys(permissionsMap ?? {}).length > 0;
    if (!stillHasCrmAccess) {
      /**
       * Someone who never had access is not being revoked — they are being refused,
       * which sign-in and the route guard already handle, and with a message that
       * says the right thing. Signing them out from here on top of that is what
       * reloaded the page out from under that message.
       */
      const hadCrmAccess = Object.keys(previous.permissions ?? {}).length > 0;
      if (!hadCrmAccess) {
        return;
      }

      parkSignOutNotice({
        title: 'Access Removed',
        message: 'Your access to this application has been removed.',
      });
      await this.logOut();
      return;
    }

    const currentUrl = this.router.url ?? '';
    const currentEntry = this._armHomePathListByPriority.find(item =>
      currentUrl.startsWith('/' + item.path.join('/')),
    );

    if (currentEntry && !hasPermissionGuard(currentEntry.entity, permissionsMap)) {
      this.toastrService.warning('Your permissions changed and you no longer have access to this page.');
      await this.router.navigate([AppRoutes.AccessDenied]);
    }
  }

  public override async logOut() {
    await signOut(this.auth);
    await super.logOut();
  }

  /**
   * Generous, because it is not a latency budget — it is the point at which we stop
   * waiting for an answer that is not coming. The identity API is on Cloud Run and a
   * cold start is seconds, so anything tight would refuse people the API was merely
   * slow to vouch for.
   */
  private static readonly ROLES_READ_TIMEOUT_MS = 30_000;

  /**
   * The signed-in agent, or null when the projection has no row for them. Bounded for
   * the same reason the roles read is: this resolves through an HTTP lookup, and a
   * login must never be able to wait on it forever.
   */
  private getLoggedInAgentOnce = () =>
    firstValueFrom(this.loggedInAgent$.pipe(timeout({ first: ARMAuthService.ROLES_READ_TIMEOUT_MS })));

  /**
   * Rejects rather than resolving false when the roles read does not arrive.
   *
   * The distinction matters: false means "we asked and this user has no CRM role", and
   * the caller answers it with Access Denied and a sign-out. A timeout means we never
   * found out, and telling someone they lack access on that basis would be a lie that
   * costs them a support ticket. The caller surfaces it as a failure to verify instead.
   */
  public checkARMPermissions = async (): Promise<boolean> => {
    const userRoles = await firstValueFrom(
      this.userRoles$.pipe(timeout({ first: ARMAuthService.ROLES_READ_TIMEOUT_MS })),
    );
    if (!Array.isArray(userRoles) || !userRoles?.length) {
      return false;
    }

    return userRoles?.some(
      role => role[UserRoleModelKeys.isEnable] && role[UserRoleModelKeys.appId] === EntityApplications.crm,
    );
  };

  protected onAgentEmailNotVerified = async () => {
    this.toastrService.error(
      '<p>To continue You need to validate the provided Email Address. <br/> Use Agent Portal for Verification.</p>',
      'Email Not Verified',
      {
        enableHtml: true,
      },
    );
  };
}
